import { Modal } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import Loader from '@/components/Atoms/Loader/Loader'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { PartnershipModel } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import InviteModal from './modals/InviteModal'
import PartnershipDetailsModal from './modals/PartnershipDetailsModal'
import PartnershipFormModal from './modals/PartnershipFormModal'
import {
  getTanstackColumns,
  PARTNERSHIPS_COLUMN_WIDTHS,
} from './PartnershipsTab.config'
import cn from './PartnershipsTab.module.scss'

function PartnershipsTab() {
  const [partnerships, setPartnerships] = useState<PartnershipModel[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [modal, contextHolder] = Modal.useModal()

  const isOrgSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const myOrganisationName = useUserInfoStore(
    (state) => state.userOrganisation?.description ?? ''
  )

  const [detailsModal, setDetailsModal] = useState<{
    open: boolean
    id: number | null
    isIncoming: boolean | null
  }>({
    open: false,
    id: null,
    isIncoming: null,
  })

  const [renewModal, setRenewModal] = useState<{
    open: boolean
    partnership: PartnershipModel | null
  }>({ open: false, partnership: null })
  const [formModal, setFormModal] = useState<{
    open: boolean
    mode: 'accept' | 'settings'
    partnership: PartnershipModel | null
  }>({ open: false, mode: 'accept', partnership: null })

  const loadData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const res = await api.get<{ partnerships: PartnershipModel[] }>(
          '/partnerships'
        )
        setPartnerships(res.data.partnerships || [])
      } catch (e) {
        handleApiError(e, modal)
      } finally {
        setLoading(false)
      }
    },
    [modal]
  )

  useEffect(() => {
    loadData()
  }, [loadData])

  const patchRow = useCallback(
    (updated: PartnershipModel) =>
      setPartnerships((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p))
      ),
    []
  )

  const handleReject = useCallback(
    async (p: PartnershipModel) => {
      const tId = toast.loading('Отклонение заявки...')
      try {
        const res = await api.post<{ partnership: PartnershipModel }>(
          `/partnerships/${p.id}/actions/reject`
        )
        patchRow(res.data.partnership)
        toast.success('Заявка отклонена', { id: tId })
      } catch (e) {
        toast.error('Ошибка', { id: tId })
        handleApiError(e, modal)
      }
    },
    [modal, patchRow]
  )

  const handleRevoke = useCallback(
    async (p: PartnershipModel) => {
      const tId = toast.loading('Отзыв заявки...')
      try {
        const res = await api.post<{ partnership: PartnershipModel }>(
          `/partnerships/${p.id}/actions/revoke`
        )
        patchRow(res.data.partnership)
        toast.success('Заявка отозвана', { id: tId })
      } catch (e) {
        toast.error('Ошибка', { id: tId })
        handleApiError(e, modal)
      }
    },
    [modal, patchRow]
  )

  const handleTerminate = useCallback(
    async (p: PartnershipModel) => {
      const confirmed = await modal.confirm({
        title: 'Расторгнуть партнёрство?',
        content: `Все сегменты партнёра на основе ваших будут архивированы. Партнёр: ${p.partner.description}`,
        okText: 'Расторгнуть',
        cancelText: 'Отмена',
        okButtonProps: { danger: true },
        centered: true,
      })
      if (!confirmed) return

      const tId = toast.loading('Расторжение...')
      try {
        const res = await api.post<{ partnership: PartnershipModel }>(
          `/partnerships/${p.id}/actions/terminate`
        )
        patchRow(res.data.partnership)
        toast.success('Партнёрство расторгнуто', { id: tId })
      } catch (e) {
        toast.error('Ошибка', { id: tId })
        handleApiError(e, modal)
      }
    },
    [modal, patchRow]
  )

  const columns = useMemo(
    () =>
      getTanstackColumns({
        myOrganisationName,
        isOrganisationSelected: isOrgSelected,
        onViewDetails: (p) =>
          setDetailsModal({
            open: true,
            id: p.id,
            isIncoming: p.status === 'PROPOSED' && !p.initiatedByMe,
          }),
        onAccept: (p) =>
          setFormModal({ open: true, mode: 'accept', partnership: p }),
        onSettings: (p) =>
          setFormModal({ open: true, mode: 'settings', partnership: p }),
        onReject: handleReject,
        onTerminate: handleTerminate,
        onRevoke: handleRevoke,
        onRenew: (p) => setRenewModal({ open: true, partnership: p }),
      }),
    [
      handleReject,
      handleTerminate,
      handleRevoke,
      myOrganisationName,
      isOrgSelected,
    ]
  )

  const normalizedSearch = search.trim().toLowerCase()
  const filteredPartnerships = partnerships.filter((partnership) =>
    partnership.partner.description.toLowerCase().includes(normalizedSearch)
  )

  return (
    <div className={cn.partnershipsTab}>
      {contextHolder}

      <PartnershipDetailsModal
        open={detailsModal.open}
        partnershipId={detailsModal.id}
        initialIsIncoming={detailsModal.isIncoming}
        onClose={() =>
          setDetailsModal({ open: false, id: null, isIncoming: null })
        }
        onEdit={() => {
          const p = partnerships.find((x) => x.id === detailsModal.id)
          if (!p) return

          setDetailsModal({ open: false, id: null, isIncoming: null })

          const isIncoming = p.status === 'PROPOSED' && !p.initiatedByMe

          setFormModal({
            open: true,
            mode: isIncoming ? 'accept' : 'settings',
            partnership: p,
          })
        }}
      />

      <div className={cn.toolbar}>
        <AtomSearchInput
          placeholder="Поиск по партнёру..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={setSearch}
          className={cn.searchInput}
          isOrganisationSelected={isOrgSelected}
        />
      </div>

      <AtomTable
        ariaLabel="Партнёрства"
        data={filteredPartnerships}
        columns={columns}
        getRowId={(partnership) => String(partnership.id)}
        columnWidths={PARTNERSHIPS_COLUMN_WIDTHS}
        isOrganisationSelected={isOrgSelected}
        emptyContent={loading ? <Loader size={24} /> : undefined}
      />

      <InviteModal
        open={renewModal.open}
        preselectedSupplier={renewModal.partnership?.partner ?? null}
        titleOverride={
          renewModal.partnership?.status === 'TERMINATED'
            ? `Возобновление партнёрства с ${renewModal.partnership.partner.description}`
            : `Повтор запроса на партнёрство с ${renewModal.partnership?.partner.description}`
        }
        onClose={() => setRenewModal({ open: false, partnership: null })}
        onSuccess={(updated) => {
          const oldId = renewModal.partnership?.id
          setRenewModal({ open: false, partnership: null })
          setPartnerships((prev) => {
            const withoutOld =
              oldId != null ? prev.filter((p) => p.id !== oldId) : prev
            const exists = withoutOld.some((p) => p.id === updated.id)
            if (exists)
              return withoutOld.map((p) => (p.id === updated.id ? updated : p))
            return [updated, ...withoutOld]
          })
        }}
        modal={modal}
      />

      <PartnershipFormModal
        open={formModal.open}
        mode={formModal.mode}
        partnership={formModal.partnership}
        onClose={() => setFormModal((s) => ({ ...s, open: false }))}
        onSuccess={(updated) => {
          setFormModal((s) => ({ ...s, open: false }))
          patchRow(updated)
        }}
        modal={modal}
      />
    </div>
  )
}

export default PartnershipsTab
